'use client';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSpotMarkets } from '../spotMarkets/useSpotMarkets';
import { useSpotMarketsLookup } from '../spotMarkets/useSpotMarketsLookup';
import useIsMainnet from '../useIsMainnet';
import { getRetryDelayFn } from '../../utils/query/retryDelay';

// todo - should this be its own section in the market selector?
// exclude bSOL here since it has its own off chain logic
const LSTS = ['mSOL', 'jitoSOL', 'INF', 'dSOL', 'dfdvSOL'];

type SANCTUM_API_RESPONSE = {
	apys: { [symbol: string]: number };
	errs: any; // just ignore these for now
};

export const useSanctumLstApiFetch = () => {
	const spotMarkets = useSpotMarkets();
	const spotMarketsLookup = useSpotMarketsLookup();
	const isMainnet = useIsMainnet();

	const LST_INDICES = useMemo(
		() =>
			spotMarketsLookup
				.filter((mkt) => LSTS.includes(mkt.symbol))
				.map((mkt) => mkt.marketIndex),
		[spotMarketsLookup]
	);

	return useQuery({
		queryKey: ['sanctumLstApy', LST_INDICES],
		queryFn: async (): Promise<SANCTUM_API_RESPONSE> => {
			const requestUrl =
				`https://extra-api.sanctum.so/v1/apy/latest?${LST_INDICES.map(
					(val, index) => {
						return `${index !== 0 ? '&lst=' : 'lst='}${
							spotMarkets.find((mkt) => mkt.marketIndex === val)?.symbol
						}`;
					}
				)}`.replaceAll(',', '');

			const response = await fetch(requestUrl);
			if (!response.ok) {
				throw new Error(`Failed to fetch LST metrics: ${response.status}`);
			}
			return response.json();
		},
		enabled: isMainnet && spotMarkets.length > 0 && LST_INDICES.length > 0,
		staleTime: 10 * 60 * 1000, // 10 minutes
		retryDelay: getRetryDelayFn(2000),
		retry: 3,
	});
};
